import { isArray } from 'lodash';
import Router from 'next/router';
import { useCallback } from 'react';

import { QueryParamNames } from '~/constants/search';
import { type Filters, type SelectedFilter } from '~/store/filters/types';
import {
  getFilterParamsNames,
  getUrlParamsFromFilters,
  getUrlParamsWithoutFilters,
} from '~/utils/filters/filtersToUrlParamsUtils';
import { getQueryWithoutParams, pushWithNewParams } from '~/utils/PathUtils';

const pushWithFilters = ({
  filters,
  pathname,
  urlParams,
}: {
  pathname: string;
  urlParams: URLSearchParams;
  // https://linear.app/air/issue/ENG-118/update-pushwithfilters-to-not-use-any
  // biome-ignore lint/suspicious/noExplicitAny: TODO - will refactor later
  filters: { [key: string]: any };
}) => {
  Object.keys(filters).forEach((filterKey) => {
    const value = filters[filterKey];
    if (isArray(value)) {
      value.forEach((filterValue) => {
        urlParams.append(filterKey, filterValue);
      });
    } else {
      urlParams.append(filterKey, filters[filterKey]);
    }
  });

  const queryString = urlParams.toString();
  const urlToGo = queryString ? `${pathname}?${queryString}` : pathname;

  return Router.push(urlToGo, undefined, { shallow: true });
};

/**
 * This hook takes selected filters and puts them in url
 */
export const useApplyFilters = () => {
  const removeFilter = useCallback(({ url = Router.asPath, filter }: { url?: string; filter: SelectedFilter }) => {
    const [pathname, query] = url.split('?');
    const newUrlParams = getQueryWithoutParams(query, getFilterParamsNames(filter));

    return pushWithNewParams(pathname, newUrlParams);
  }, []);

  const removeFilters = useCallback(({ url = Router.asPath, filters }: { url?: string; filters: SelectedFilter[] }) => {
    const [pathname, query] = url.split('?');

    const newUrlParams = getQueryWithoutParams(query, filters.flatMap(getFilterParamsNames));

    return pushWithNewParams(pathname, newUrlParams);
  }, []);

  // Purposefully bypasses getFilterParamsNames and other checks to ensure that the passed filters are applied exactly as is
  // and also removing the existing filters
  const replaceFilters = useCallback(
    ({ url = Router.asPath, filters = {} }: { url?: string; filters?: Partial<Filters> }) => {
      const appliedFilters = getUrlParamsFromFilters(filters);
      const [pathname] = url.split('?');

      return pushWithFilters({ pathname, urlParams: new URLSearchParams(), filters: appliedFilters });
    },
    [],
  );

  const applyFilters = useCallback(
    ({ url = Router.asPath, filters = {} }: { url?: string; filters?: Partial<Filters> }) => {
      const appliedFilters = getUrlParamsFromFilters(filters);

      const [pathname, query] = url.split('?');
      const newUrlParams = getUrlParamsWithoutFilters(query);

      return pushWithFilters({ pathname, urlParams: newUrlParams, filters: appliedFilters });
    },
    [],
  );

  const clearFilters = useCallback(() => applyFilters({ filters: {} }), [applyFilters]);

  const clearSearchAndFilters = useCallback(() => {
    const [pathname, query] = Router.asPath.split('?');
    const queryWithoutFilters = getUrlParamsWithoutFilters(query).toString();
    const queryWithoutSearch = getQueryWithoutParams(queryWithoutFilters, [QueryParamNames.Search]).toString();

    const urlToGo = queryWithoutSearch ? `${pathname}?${queryWithoutSearch}` : pathname;

    return Router.push(urlToGo, undefined, { shallow: true });
  }, []);

  return {
    applyFilters,
    removeFilter,
    removeFilters,
    clearFilters,
    clearSearchAndFilters,
    replaceFilters,
  };
};
